//libraries
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { notify } from "../../../../../services/toast.config";

//components
import TiptapEditor from "../../../common/components/editor/TiptapEditor";

//helpers
import api from "../../../../../services/api";
import { uploadFileToCloudinary } from "../../../../../utils/uploadFileToCloudinary";
import { all_routes } from "../../../../router/all_routes";
import { humanReadableDate } from "../../../../../helper/dateTimeHelper";
import { ConfigProvider, Select } from "antd";
import { getEmployeeOptions } from "../../../reports-settings/report-type/helpers/form.handler";

//types
interface CloudinaryAttachment {
  id: string;
  url: string;
  name: string;
  size: number;
  public_id: string;
  resource_type: string;
}

interface EditReportComponentProps {
  reportData?: any;
  allUsers?: any;
  upcomingReportType?: any[];
}

const EditReportComponent: React.FC<EditReportComponentProps> = ({
  reportData,
  allUsers,
  upcomingReportType = [],
}) => {
  const navigate = useNavigate();
  const signature = localStorage.getItem("signature");
  const maxFileSize = process.env.REACT_APP_ERS_MAX_FILE_SIZE_MB
    ? parseInt(process.env.REACT_APP_ERS_MAX_FILE_SIZE_MB)
    : 5;
  const maxAttachments = process.env.REACT_APP_ERS_MAX_ATTACHMENTS
    ? parseInt(process.env.REACT_APP_ERS_MAX_ATTACHMENTS)
    : 5;

  // STATES
  const [content, setContent] = useState<string>("");
  const [reportTitle, setReportTitle] = useState<string>("");
  const [showsTo, setShowsTo] = useState<any>([]);

  const [attachments, setAttachments] = useState<CloudinaryAttachment[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCustomReportInitial, setIsCustomReportInitial] = useState<boolean>(true);
  const [isCustomReport, setIsCustomReport] = useState<boolean>(true);
  const [selectedReportTypeId, setSelectedReportTypeId] = useState<number>(-1);
  const [selectedReportTypeIndex, setSelectedReportTypeIndex] = useState<number>(-1);
  const [selectedDueDate, setSelectedDueDate] = useState<any>(null);

  // Header display data - read-only from reportData
  const headerData = reportData
    ? {
      title: reportData.report_title || "Untitled Report",
      reportDate: reportData.report_date
        ? humanReadableDate(reportData.report_date)
        : humanReadableDate(reportData.created_at) || "N/A",
      dueDate: reportData.due_date
        ? humanReadableDate(reportData.due_date)
        : humanReadableDate(reportData.edit_deadline) || "N/A",
      frequencyType: reportData.frequency_type
        ? reportData.frequency_type.charAt(0).toUpperCase() +
        reportData.frequency_type.slice(1)
        : reportData.report_type_id === -1
          ? "Custom"
          : "Unknown",
      reportTypeName:
        reportData.report_type_name ||
        (reportData.report_type_id === -1
          ? "Custom Report"
          : "Unknown Report Type"),
    }
    : null;

  // Initialize data from reportData
  useEffect(() => {
    if (reportData && !isInitialized) {
      try {
        const _isCustomReportInitial = reportData?.report_type_id == -1 ? true : false;
        setIsCustomReportInitial(_isCustomReportInitial);
        setIsCustomReport(_isCustomReportInitial);
        setSelectedReportTypeId(reportData?.report_type_id);
        setSelectedReportTypeIndex(-1);
        setSelectedDueDate(reportData?.due_date || null);

        setReportTitle(reportData?.report_title);
        setContent(reportData.report_content);
        setShowsTo(reportData?.shows_to);

        // Process existing attachments - use the correct field name
        let existingAttachments: CloudinaryAttachment[] = [];

        // Check multiple possible attachment field names
        const attachmentSources = [
          reportData.attachments,
          reportData.report_attachments,
          reportData.files,
        ];

        for (const source of attachmentSources) {
          if (source && Array.isArray(source) && source.length > 0) {
            existingAttachments = source.map((att: any, index: number) => ({
              id: att.id || `existing-${index}-${Date.now()}`,
              url: att.url || att.secure_url || att.file_url || "",
              name:
                att.name ||
                att.original_filename ||
                att.filename ||
                `File ${index + 1}`,
              size: att.size || att.bytes || 0,
              public_id: att.public_id || "",
              resource_type: att.resource_type || "auto",
            }));
            break;
          }
        }

        setAttachments(existingAttachments);
        setIsInitialized(true);
      } catch (error) {
        notify.error("Error loading report data");
      }
    }
  }, [reportData, isInitialized]);

  // HANDLERS
  const handleContentChange = (html: string): void => {
    setContent(html);
  };

  const handleReportTypeChange = (idx: number) => {
    setSelectedReportTypeIndex(idx);

    // Update header dynamically based on selection
    if (idx === -1) {
      setSelectedReportTypeId(-1);
      setIsCustomReport(true);
      setSelectedDueDate(null);
      if (reportData?.report_type_id === -1) {
        setReportTitle(reportData?.report_title || "Untitled Custom Report"); // Reset to original or custom title
      } else {
        setReportTitle("Untitled Custom Report");
      }
    } else {
      setIsCustomReport(false);
      const selected = upcomingReportType?.[idx];
      if (selected) {
        setSelectedReportTypeId(selected.id);
        setReportTitle(selected.name);
        setSelectedDueDate(selected.due_date);
      }
    }
  };

  // File validation
  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File exceeds ${maxFileSize}MB limit`;
    }
    return null;
  };

  // Handle file selection with immediate upload to Cloudinary
  const handleFileSelect = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newErrors: string[] = [];

    if (attachments.length + fileArray.length > maxAttachments) {
      setErrors([`Maximum ${maxAttachments} files allowed`]);
      return;
    }

    setIsUploading(true);
    setErrors([]);

    const uploadPromises: Promise<CloudinaryAttachment | null>[] =
      fileArray.map((file, index) => {
        const error = validateFile(file);
        if (error) {
          newErrors.push(error);
          return Promise.resolve(null);
        }

        // Check for duplicates
        const isDuplicate = attachments.some(
          (att) => att.name === file.name && att.size === file.size
        );

        if (isDuplicate) {
          newErrors.push(`${file.name} is already attached`);
          return Promise.resolve(null);
        }

        // Upload to Cloudinary
        return uploadFileToCloudinary({
          file,
          folder: "/ers/reports/attachments",
        })
          .then((response) => ({
            id: `new-${Date.now()}-${index}`,
            url: response.secure_url,
            name: file.name,
            size: file.size,
            public_id: response.public_id,
            resource_type: response.resource_type,
          }))
          .catch((error) => {
            console.error(`Upload failed for ${file.name}:`, error);
            newErrors.push(`Failed to upload ${file.name}`);
            return null;
          });
      });

    try {
      const uploadResults = await Promise.all(uploadPromises);
      const successfulUploads = uploadResults.filter(
        (result): result is CloudinaryAttachment => result !== null
      );

      if (successfulUploads.length > 0) {
        setAttachments((prev) => [...prev, ...successfulUploads]);
        notify.success(
          `${successfulUploads.length} file(s) uploaded successfully`
        );
      }

      if (newErrors.length > 0) {
        setErrors(newErrors);
        notify.error(`${newErrors.length} file(s) failed to upload`);
      }
    } catch (error) {
      console.error("Upload process failed:", error);
      notify.error("Upload process failed");
      setErrors(["Upload process failed"]);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Remove attachment
  const removeAttachment = async (attachmentId: string) => {
    const attachmentToRemove = attachments.find(
      (att) => att.id === attachmentId
    );
    if (!attachmentToRemove) return;

    // Optimistically update UI
    setAttachments(attachments.filter((att) => att.id !== attachmentId));
    setErrors([]);

    try {
      // Only delete from Cloudinary if it has a public_id
      if (attachmentToRemove.public_id) {
        await api.post("ers/cloudinary/deleteFile", {
          signature: signature,
          public_id: attachmentToRemove.public_id,
          resource_type: attachmentToRemove.resource_type || "auto",
        });
      }
      notify.success("File removed successfully");
    } catch (error) {
      console.error("Failed to delete file:", error);
      setAttachments((prev) => [...prev, attachmentToRemove]);
      notify.error("Failed to delete file. Please try again.");
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 10) / 10 + sizes[i];
  };

  // Handle submit - Update existing report
  const handleSubmit = async (status: "draft" | "submitted") => {
    if (!reportData) {
      notify.error("Report data not loaded");
      return;
    }

    if (!content.trim()) {
      notify.error("Report content is required");
      return;
    }

    setIsSubmitting(true);

    console.log("Selected Due Date: ", selectedDueDate)

    const updateData = {
      signature: signature,
      report_title: reportTitle,
      report_id: reportData.id,
      report_type_id: selectedReportTypeId,
      report_content: content.trim(),
      attachments: attachments,
      is_draft: status === "draft",
      shows_to: showsTo,
      status: status === "draft" ? 0 : 1,
      report_date: selectedDueDate || reportData?.report_date,
    };

    try {
      const response = await api.post("ers/reports/updateReport", updateData);
      notify.success(response?.data?.message || "Report updated successfully");

      // Navigate back after successful update
      setTimeout(() => {
        navigate(all_routes.ersReportsOverview);
      }, 1000);
    } catch (e: any) {
      console.error("Error updating report:", e);
      const error = e?.data;
      let errorMessage = "Internal Server Error!";

      if (Array.isArray(error?.message) && error?.message[0]?.msg) {
        errorMessage = error.message[0].msg;
      } else if (typeof error?.message === "string" && error.message.trim()) {
        errorMessage = error.message;
      }

      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (!reportData || !headerData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "400px" }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-muted">Loading report data...</h5>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Document-Style Header - Read-only */}
      <div className="card mb-4 border-0 shadow-lg">
        <div className="card-body px-4 py-4">
          <div className="mb-1">
            <div className="d-flex flex-wrap justify-content-between align-items-start ">
              <div style={{ width: "500px" }}>
                {isCustomReport ? (
                  <input
                    type="text"
                    value={reportTitle}
                    onChange={(e: any) => setReportTitle(e.target.value)}
                    className="form-control form-control-sm border-0 bg-transparent p-0   text-primary"
                    style={{
                      outline: "none",
                      boxShadow: "none",
                      fontWeight: 500,
                      fontSize: "20px",
                      lineHeight: "2",
                    }}
                    placeholder="Enter custom report title..."
                  />
                ) : (
                  <h4
                    className="text-primary fs-20 fw-normal mb-0"
                    style={{ lineHeight: "2" }}>
                    {reportTitle}
                  </h4>
                )}

                <div className="d-flex align-items-center gap-4 text-muted small">
                  <span>
                    <i className="ti ti-calendar-event me-1"></i>
                    <strong>Date:</strong> {headerData.reportDate}
                  </span>
                  <span>
                    <i className="ti ti-clock me-1"></i>
                    <strong>Due:</strong> {headerData.dueDate}
                  </span>
                  <span>
                    <i className="ti ti-repeat me-1"></i>
                    <strong>Type:</strong> {headerData.frequencyType}
                  </span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-start ">
                {isCustomReport && (
                  <div>
                    <ConfigProvider
                      theme={{
                        components: {
                          Select: {
                            multipleItemHeight: 28,
                            multipleItemBg: "#4c1d95",
                            multipleItemBorderColor: "#7c3aed",
                            colorText: "#ede9fe",
                            colorTextPlaceholder: "#9ca3af",
                            colorBgContainer: "#ffffff",
                            colorTextBase: "#1f2937",
                            zIndexPopup: 1060,
                          },
                        },
                      }}>
                      <Select
                        mode="multiple"
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        style={{
                          width: "330px",
                        }}
                        placeholder="Share with other users..."
                        value={showsTo}
                        onChange={(selectedKeys) => {
                          setShowsTo(selectedKeys);
                        }}
                        options={getEmployeeOptions(allUsers)}
                        maxTagCount="responsive"
                        optionRender={(option) => (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "0",
                            }}>
                            <img
                              src={
                                option.data.image_url ||
                                "assets/img/users/user.png"
                              }
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                flexShrink: 0,
                              }}
                            />
                            <span
                              style={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}>
                              {option.label}
                            </span>
                          </div>
                        )}
                      />
                    </ConfigProvider>
                  </div>
                )}

                <div className="ms-3">
                  <select
                    id="reportType"
                    className="form-select form-select-sm"
                    onChange={(e) => handleReportTypeChange(parseInt(e.target.value))}
                    value={!isCustomReportInitial ? selectedReportTypeId : selectedReportTypeIndex}
                    disabled={!isCustomReportInitial}>

                    {/* Preserve original behavior if it's not a custom report */}
                    {!isCustomReportInitial && (
                      <option value={reportData?.report_type_id}>
                        {reportData?.report_type_name}
                      </option>
                    )}

                    {/* Show selectable options if it was initially a custom report */}
                    {isCustomReportInitial && (
                      <>
                        <option value="-1">Other Report Type</option>
                        {upcomingReportType?.map((type: any, idx: number) => (
                          <option
                            key={`${type.id}-${idx}-${type.due_date}`}
                            value={idx}>
                            {`${type.name} (${humanReadableDate(
                              type?.due_date
                            )})`}
                          </option>
                        ))}
                      </>
                    )}

                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TipTap Editor */}
      {reportData && (
        <TiptapEditor
          content={content}
          onChange={handleContentChange}
          placeholder="Edit your report content..."
          className="w-100"
          showTemplates={false}
        />
      )}

      {/* Report Footer */}
      <div className="card report-footer shadow-lg">
        <div className="report-footer__content">
          {/* File Upload Section */}
          <div className="report-footer__upload">
            <button
              type="button"
              className="report-footer__upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}>
              {isUploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="ti ti-paperclip"></i>
                  <span>Attach Files</span>
                </>
              )}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) =>
                e.target.files && handleFileSelect(e.target.files)
              }
              className="d-none"
            />

            <small className="report-footer__help">
              <span className="text-danger"> Max {maxFileSize}MB each</span>
            </small>
          </div>

          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="report-footer__errors">
              {errors.map((error, index) => (
                <span key={index} className="report-footer__error">
                  {error}
                </span>
              ))}
            </div>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="report-footer__attachments">
              {attachments.map((attachment) => (
                <div key={attachment.id} className="attachment-tag">
                  <div className="attachment-tag__content">
                    <i className="ti ti-file attachment-tag__icon"></i>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="attachment-tag__name text-decoration-none">
                      {attachment.name}
                    </a>
                    <span className="attachment-tag__size">
                      ({formatFileSize(attachment.size)})
                    </span>
                  </div>
                  <button
                    type="button"
                    className="attachment-tag__remove"
                    onClick={() => removeAttachment(attachment.id)}
                    title={`Remove ${attachment.name}`}>
                    <i className="ti ti-x"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="report-footer__actions">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => handleSubmit("draft")}
              disabled={
                isSubmitting ||
                isUploading ||
                !reportData.can_edit ||
                !reportData.is_draft
              }>
              <i className="ti ti-device-floppy me-1"></i>
              Save as Draft
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleSubmit("submitted")}
              disabled={isSubmitting || isUploading || !reportData.can_edit}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className="ti ti-edit me-1"></i>
                  {reportData.is_draft ? <>Submit Draft</> : <>Update Report</>}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditReportComponent;
